import { createApiInstance } from '@/api/createApiInstance';
import { IS_V2_WRITE_ENABLED } from '@/configs/v2Write';
import { ItemResponse, ItemsResponse } from '@/api/types';
import {
  Board,
  BoardSearchRequest,
  Comment,
  CreateBoardRequest,
  CreateCommentRequest,
  CreateSprintRequest,
  CreateTaskLinksRequest,
  CreateTaskRequest,
  CreateTimelogRequest,
  PageResponse,
  Queue,
  QueueMember,
  QueueSearchRequest,
  Sprint,
  SprintSearchRequest,
  SuggestRequest,
  Task,
  TaskBasic,
  TaskLink,
  TaskPriority,
  TaskSearchRequest,
  TaskStatus,
  TaskSuggestRequest,
  TaskType,
  Timelog,
  TimelogSearchRequest,
  UpdateBoardRequest,
  UpdateCommentRequest,
  UpdateSprintRequest,
  UpdateTaskRequest,
  UpdateTimelogRequest,
  User,
  UserSearchRequest,
  UserSuggestRequest,
} from '@/tracker/types';

// Контракт: docs/tracker/openapi.yaml (бэкенд) + черновик фронта в docs/tracker/api.md.
// Методы добавляются по мере появления экранов
const PREFIX = '/api/v2';
const MAX_PAGE_SIZE = 200;

const queueUrl = (key: string) => `${PREFIX}/queues/${encodeURIComponent(key)}`;
const taskUrl = (key: string) => `${PREFIX}/tasks/${encodeURIComponent(key)}`;
const boardUrl = (id: string) => `${PREFIX}/boards/${encodeURIComponent(id)}`;
const sprintUrl = ({ boardId, sprintId }: { boardId: string; sprintId: string }) =>
  `${boardUrl(boardId)}/sprints/${encodeURIComponent(sprintId)}`;

class TrackerApi {
  private axiosInstance = createApiInstance('tracker', { readOnly: !IS_V2_WRITE_ENABLED });

  async searchTasks(request: TaskSearchRequest): Promise<PageResponse<TaskBasic>> {
    const response = await this.axiosInstance.post<PageResponse<TaskBasic>>(
      `${PREFIX}/tasks:search`,
      request,
    );

    return response.data;
  }

  // shortcut: выкачивает все страницы. Для длинных списков — бесконечная прокрутка по nextPageToken
  async searchAllTasks(request: Omit<TaskSearchRequest, 'pageToken'>): Promise<TaskBasic[]> {
    return this.collectPages((pageToken) =>
      this.searchTasks({ pageSize: MAX_PAGE_SIZE, ...request, pageToken }),
    );
  }

  /** Выбор задачи при вводе: начало ключа, слова по началу; без ввода — мои недавние */
  async suggestTasks(request: TaskSuggestRequest): Promise<TaskBasic[]> {
    const response = await this.axiosInstance.post<ItemsResponse<TaskBasic>>(
      `${PREFIX}/tasks:suggest`,
      request,
    );

    return response.data.items;
  }

  async suggestUsers(request: UserSuggestRequest): Promise<User[]> {
    const response = await this.axiosInstance.post<ItemsResponse<User>>(
      `${PREFIX}/users:suggest`,
      request,
    );

    return response.data.items;
  }

  async suggestQueues(request: SuggestRequest): Promise<Queue[]> {
    const response = await this.axiosInstance.post<ItemsResponse<Queue>>(
      `${PREFIX}/queues:suggest`,
      request,
    );

    return response.data.items;
  }

  async suggestBoards(request: SuggestRequest): Promise<Board[]> {
    const response = await this.axiosInstance.post<ItemsResponse<Board>>(
      `${PREFIX}/boards:suggest`,
      request,
    );

    return response.data.items;
  }

  async getTask(key: string): Promise<Task> {
    const response = await this.axiosInstance.get<ItemResponse<Task>>(taskUrl(key));

    return response.data.item;
  }

  async createTask(data: CreateTaskRequest): Promise<Task> {
    const response = await this.axiosInstance.post<ItemResponse<Task>>(`${PREFIX}/tasks`, data);

    return response.data.item;
  }

  async updateTask({ key, data }: { key: string; data: UpdateTaskRequest }): Promise<Task> {
    const response = await this.axiosInstance.patch<ItemResponse<Task>>(taskUrl(key), data);

    return response.data.item;
  }

  /** Все комментарии задачи, по порядку создания */
  async listAllComments(key: string): Promise<Comment[]> {
    return this.collectPages(async (pageToken) => {
      const response = await this.axiosInstance.get<PageResponse<Comment>>(
        `${taskUrl(key)}/comments`,
        { params: { pageSize: MAX_PAGE_SIZE, pageToken } },
      );

      return response.data;
    });
  }

  async createComment({
    key,
    data,
  }: {
    key: string;
    data: CreateCommentRequest;
  }): Promise<Comment> {
    const response = await this.axiosInstance.post<ItemResponse<Comment>>(
      `${taskUrl(key)}/comments`,
      data,
    );

    return response.data.item;
  }

  /** Только автор; устаревший `etag` — 409 `comment_conflict` */
  async updateComment({
    key,
    id,
    data,
  }: {
    key: string;
    id: string;
    data: UpdateCommentRequest;
  }): Promise<Comment> {
    const response = await this.axiosInstance.patch<ItemResponse<Comment>>(
      `${taskUrl(key)}/comments/${encodeURIComponent(id)}`,
      data,
    );

    return response.data.item;
  }

  async deleteComment({ key, id }: { key: string; id: string }): Promise<void> {
    await this.axiosInstance.delete(`${taskUrl(key)}/comments/${encodeURIComponent(id)}`);
  }

  /** Всё или ничего; ответ — созданные связи с точки зрения задачи `key` */
  async createTaskLinks({
    key,
    data,
  }: {
    key: string;
    data: CreateTaskLinksRequest;
  }): Promise<TaskLink[]> {
    const response = await this.axiosInstance.post<ItemsResponse<TaskLink>>(
      `${taskUrl(key)}/links:batchCreate`,
      data,
    );

    return response.data.items;
  }

  /** Связь удаляется с любой стороны, вторая задача может быть недоступна */
  async deleteTaskLinks({ key, ids }: { key: string; ids: string[] }): Promise<void> {
    await this.axiosInstance.post(`${taskUrl(key)}/links:batchDelete`, { ids });
  }

  /** Страница логов: например, логи задачи — `filters.task.eq` */
  async searchTimelogs(request: TimelogSearchRequest): Promise<PageResponse<Timelog>> {
    const response = await this.axiosInstance.post<PageResponse<Timelog>>(
      `${PREFIX}/timelogs:search`,
      request,
    );

    return response.data;
  }

  async createTimelog(data: CreateTimelogRequest): Promise<Timelog> {
    const response = await this.axiosInstance.post<ItemResponse<Timelog>>(
      `${PREFIX}/timelogs`,
      data,
    );

    return response.data.item;
  }

  async getTimelog(id: string): Promise<Timelog> {
    const response = await this.axiosInstance.get<ItemResponse<Timelog>>(
      `${PREFIX}/timelogs/${encodeURIComponent(id)}`,
    );

    return response.data.item;
  }

  /** Правка на месте: перенос на другую задачу или дату не меняет `id` */
  async updateTimelog({ id, data }: { id: string; data: UpdateTimelogRequest }): Promise<Timelog> {
    const response = await this.axiosInstance.patch<ItemResponse<Timelog>>(
      `${PREFIX}/timelogs/${encodeURIComponent(id)}`,
      data,
    );

    return response.data.item;
  }

  async deleteTimelog(id: string): Promise<void> {
    await this.axiosInstance.delete(`${PREFIX}/timelogs/${encodeURIComponent(id)}`);
  }

  async searchUsers(request: UserSearchRequest): Promise<PageResponse<User>> {
    const response = await this.axiosInstance.post<PageResponse<User>>(
      `${PREFIX}/users:search`,
      request,
    );

    return response.data;
  }

  async searchQueues(request: QueueSearchRequest): Promise<PageResponse<Queue>> {
    const response = await this.axiosInstance.post<PageResponse<Queue>>(
      `${PREFIX}/queues:search`,
      request,
    );

    return response.data;
  }

  /** Все доступные очереди: для выбора очереди при создании задачи */
  async searchAllQueues(request: Omit<QueueSearchRequest, 'pageToken'>): Promise<Queue[]> {
    return this.collectPages((pageToken) =>
      this.searchQueues({ pageSize: MAX_PAGE_SIZE, ...request, pageToken }),
    );
  }

  async getQueue(key: string): Promise<Queue> {
    const response = await this.axiosInstance.get<ItemResponse<Queue>>(queueUrl(key));

    return response.data.item;
  }

  /** Участники очереди по времени выдачи доступа */
  async listQueueMembers(key: string): Promise<QueueMember[]> {
    const response = await this.axiosInstance.get<ItemsResponse<QueueMember>>(
      `${queueUrl(key)}/members`,
    );

    return response.data.items;
  }

  /** Только админ; уже бывших участников пропускает. Ответ — участники для всех `userIds` */
  async addQueueMembers({
    queueKey,
    userIds,
  }: {
    queueKey: string;
    userIds: string[];
  }): Promise<QueueMember[]> {
    const response = await this.axiosInstance.post<ItemsResponse<QueueMember>>(
      `${queueUrl(queueKey)}/members:batchCreate`,
      { userIds },
    );

    return response.data.items;
  }

  /** Только админ; идемпотентно */
  async removeQueueMembers({
    queueKey,
    userIds,
  }: {
    queueKey: string;
    userIds: string[];
  }): Promise<void> {
    await this.axiosInstance.post(`${queueUrl(queueKey)}/members:batchDelete`, { userIds });
  }

  async searchBoards(request: BoardSearchRequest): Promise<PageResponse<Board>> {
    const response = await this.axiosInstance.post<PageResponse<Board>>(
      `${PREFIX}/boards:search`,
      request,
    );

    return response.data;
  }

  async searchAllBoards(request: Omit<BoardSearchRequest, 'pageToken'>): Promise<Board[]> {
    return this.collectPages((pageToken) =>
      this.searchBoards({ pageSize: MAX_PAGE_SIZE, ...request, pageToken }),
    );
  }

  async getBoard(id: string): Promise<Board> {
    const response = await this.axiosInstance.get<ItemResponse<Board>>(boardUrl(id));

    return response.data.item;
  }

  async createBoard(data: CreateBoardRequest): Promise<Board> {
    const response = await this.axiosInstance.post<ItemResponse<Board>>(`${PREFIX}/boards`, data);

    return response.data.item;
  }

  async updateBoard({ id, data }: { id: string; data: UpdateBoardRequest }): Promise<Board> {
    const response = await this.axiosInstance.patch<ItemResponse<Board>>(boardUrl(id), data);

    return response.data.item;
  }

  /** Мягкое удаление; владелец доски или админ */
  async deleteBoard(id: string): Promise<void> {
    await this.axiosInstance.delete(boardUrl(id));
  }

  /** Поставить задачи на доску; идемпотентно, владельцем доски быть не нужно */
  async addTasksToBoard({ boardId, keys }: { boardId: string; keys: string[] }): Promise<void> {
    await this.axiosInstance.post(`${boardUrl(boardId)}:addTasks`, { tasks: keys });
  }

  /** Снять задачи с доски; из спринтов этой доски они уходят тоже */
  async removeTasksFromBoard({
    boardId,
    keys,
  }: {
    boardId: string;
    keys: string[];
  }): Promise<void> {
    await this.axiosInstance.post(`${boardUrl(boardId)}:removeTasks`, { tasks: keys });
  }

  async createSprint({
    boardId,
    data,
  }: {
    boardId: string;
    data: CreateSprintRequest;
  }): Promise<Sprint> {
    const response = await this.axiosInstance.post<ItemResponse<Sprint>>(
      `${boardUrl(boardId)}/sprints`,
      data,
    );

    return response.data.item;
  }

  async updateSprint({
    boardId,
    sprintId,
    data,
  }: {
    boardId: string;
    sprintId: string;
    data: UpdateSprintRequest;
  }): Promise<Sprint> {
    const response = await this.axiosInstance.patch<ItemResponse<Sprint>>(
      sprintUrl({ boardId, sprintId }),
      data,
    );

    return response.data.item;
  }

  /** Только черновик */
  async deleteSprint(ids: { boardId: string; sprintId: string }): Promise<void> {
    await this.axiosInstance.delete(sprintUrl(ids));
  }

  /** `draft` → `active`; на доске активный спринт один */
  async startSprint(ids: { boardId: string; sprintId: string }): Promise<Sprint> {
    const response = await this.axiosInstance.post<ItemResponse<Sprint>>(`${sprintUrl(ids)}:start`);

    return response.data.item;
  }

  /** `active` → `closed`; незавершённые задачи — в `moveUnfinishedTo` или, если null, в бэклог */
  async closeSprint({
    boardId,
    sprintId,
    moveUnfinishedTo,
  }: {
    boardId: string;
    sprintId: string;
    moveUnfinishedTo: string | null;
  }): Promise<Sprint> {
    const response = await this.axiosInstance.post<ItemResponse<Sprint>>(
      `${sprintUrl({ boardId, sprintId })}:close`,
      { moveUnfinishedTo },
    );

    return response.data.item;
  }

  /** Идемпотентно; задача уходит в бэклог доски */
  async removeTasksFromSprint({
    boardId,
    sprintId,
    keys,
  }: {
    boardId: string;
    sprintId: string;
    keys: string[];
  }): Promise<void> {
    await this.axiosInstance.post(`${sprintUrl({ boardId, sprintId })}:removeTasks`, {
      tasks: keys,
    });
  }

  /** Идемпотентно; задача уходит из другого спринта той же доски */
  async addTasksToSprint({
    boardId,
    sprintId,
    keys,
  }: {
    boardId: string;
    sprintId: string;
    keys: string[];
  }): Promise<void> {
    await this.axiosInstance.post(`${sprintUrl({ boardId, sprintId })}:addTasks`, {
      tasks: keys,
    });
  }

  /** `boardId: '-'` — спринты всех досок */
  async searchSprints({
    boardId,
    request,
  }: {
    boardId: string;
    request: SprintSearchRequest;
  }): Promise<PageResponse<Sprint>> {
    const response = await this.axiosInstance.post<PageResponse<Sprint>>(
      `${boardUrl(boardId)}/sprints:search`,
      request,
    );

    return response.data;
  }

  async searchAllSprints({
    boardId,
    request,
  }: {
    boardId: string;
    request: Omit<SprintSearchRequest, 'pageToken'>;
  }): Promise<Sprint[]> {
    return this.collectPages((pageToken) =>
      this.searchSprints({ boardId, request: { pageSize: MAX_PAGE_SIZE, ...request, pageToken } }),
    );
  }

  /** Справочник статусов целиком, без пагинации */
  async listStatuses(): Promise<TaskStatus[]> {
    const response = await this.axiosInstance.get<ItemsResponse<TaskStatus>>(
      `${PREFIX}/taskStatuses`,
    );

    return response.data.items;
  }

  /** Справочник типов задач целиком, без пагинации */
  async listTaskTypes(): Promise<TaskType[]> {
    const response = await this.axiosInstance.get<ItemsResponse<TaskType>>(`${PREFIX}/taskTypes`);

    return response.data.items;
  }

  /** Справочник приоритетов целиком, без пагинации */
  async listPriorities(): Promise<TaskPriority[]> {
    const response = await this.axiosInstance.get<ItemsResponse<TaskPriority>>(
      `${PREFIX}/taskPriorities`,
    );

    return response.data.items;
  }

  private async collectPages<T>(
    fetchPage: (pageToken: string | undefined) => Promise<PageResponse<T>>,
  ): Promise<T[]> {
    const items: T[] = [];
    let pageToken: string | undefined;

    do {
      const page = await fetchPage(pageToken);

      items.push(...page.items);
      pageToken = page.nextPageToken ?? undefined;
    } while (pageToken);

    return items;
  }
}

export const trackerApi = new TrackerApi();
